import moment = require("moment");
import _ = require("underscore");
import {none, option, Option} from "ts-option";
import {Moment} from "moment";

export default class DateUtils {

	public static DATE_FORMAT = "ddd D MMM YYYY";
	public static DMY_DATE_FORMAT = "DD/MM/YYYY";
	public static REMINDER_DATE_FORMAT = "ddd DD/MM/YY";
	public static REMINDER_TIME_FORMAT = "hh:mma";
	public static TIME_FORMAT = "hh:mm A";
	public static DATE_TIME_FORMAT = `${DateUtils.DATE_FORMAT} ${DateUtils.TIME_FORMAT}`;
	public static ISO_DATE_FORMAT = "YYYY-MM-DD";
	public static DAILY_PLAN_DATE_FORMAT = "D MMM YYYY";
	public static DAILY_PLAN_DATE_FORMAT_WO_YEAR = "D MMM";
	public static ONLY_YEAR_FORMAT = "YYYY";
	public static DAILY_PLAN_TIME_FORMAT = "HH:mm";
	public static NAME_OF_DAY_ONLY_FORMAT = "dddd";
	public static JOB_COMPLETE_DATE_FORMAT = `DD-MM-YYYY hh:mm:ss A`;
	public static FULL_MONTH_NAME_DATE_FORMAT = "D MMMM YYYY";
	public static INVOICE_TIME_DATE_FORMAT = "D MMM YYYY, hh:mm a";

	/**
	 * Regex rule for parsing 'DD/MM/YYYY' format dates.
	 * @type {RegExp}
	 */
	public static DMY_DATE_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/;

	/**
	 * Render formatted period message.
	 * @param {moment.Duration} duration
	 * @return {string}
	 */
	public static renderPeriodMessage(duration: moment.Duration): string {
		const years: number = duration.years();
		const months: number = duration.months();
		const yearMessage: string = `${years} ${years === 1 ? "year " : "years "}`;
		const monthMessage: string = `${months} ${months === 1 ? "month" : "months"}`;
		return `${years ? yearMessage : ""}${months ? monthMessage : ""}`;
	}

	/**
	 * Return first available date in the future with given interval of time from the beginning of the day.
	 * Example:
	 * given: 12:00, interval = 30, now is 12:12 returns 12:30
	 * given: 12:35, interval = 30, now is 12:12 returns 12:35
	 * given: 00:35, interval = 45, now is 00:20 returns 00:35
	 * given: 00:35, interval = 45, now is 00:40 returns 00:45
	 * given: 00:35, interval = 45, now is 00:46 returns 01:15
	 * @param {moment.Moment} date date to check if it's available
	 * @param {number} interval interval in minutes from full hour to be returned.
	 * @param {moment.Moment} now current time. Needed to pass for testing purpose.
	 * @return {moment.Moment}
	 */
	public static firstInFutureByInterval(date: moment.Moment, interval: number, now: moment.Moment = moment()): moment.Moment {
		if (date.isBefore(now)) {
			for (let t = 0; t <= 24 * 60; t += interval) {
				const timeWithInterval = moment(now).startOf("day").add(t, "m");
				if (timeWithInterval.isAfter(now)) {
					return timeWithInterval;
				}
			}
		}

		return date;
	}


	/**
	 * Parse range string in format '[-]{number}d/w/y' and create new date adding / subtracting proper time period.
	 * Example. inputString: -2w = moment.now().subtract({amount: 2, unit: "weeks"}
	 * Example. inputString: 1d = moment.now().add({amount: 1, unit: "day"}
	 * Example. inputString: -12y = moment.now().subtract({amount: 12, unit: "years"}
	 * @param inputString input value to parse.
	 * @param date optional date to get as base date.
	 * @return return if value is parsable return created date. None if value is too short for parsing. String array with input
	 * values that doesn't match pattern
	 */
	public static parseRangeString(inputString: string, date: moment.Moment = moment()): Option<moment.Moment> | string[] {
		if ((inputString[0] === "-" && inputString.length < 3) || inputString.length < 2) {
			return none;
		}
		const arrayOfStrings = inputString.split(" ");
		const filteredArray: string[] = [];
		const incorrectValuesArray: string[] = [];
		arrayOfStrings.forEach(function(arrayItem) {
			if (/^[-]?\d+[mhdwMQy]$/.test(arrayItem)) {
				filteredArray.push(arrayItem);
			}
			else {
				incorrectValuesArray.push(arrayItem);
			}
		});
		if (incorrectValuesArray.length > 0) {
			return incorrectValuesArray;
		}
		const objectFromFilteredArray: { [key: string]: number } = {};
		filteredArray.forEach(function(arrayItem) {
			const match = arrayItem.match(/(\d+)(\w)/);
			if (objectFromFilteredArray[match[2]]) {
				objectFromFilteredArray[match[2]] = Number(objectFromFilteredArray[match[2]]) + Number(match[1])
			}
			else {
				objectFromFilteredArray[match[2]] = Number(match[1]);
			}
		});
		const productDate = inputString[0] === "-" ? moment(date).subtract(objectFromFilteredArray) :
			moment(date).add(objectFromFilteredArray);
		return (option(productDate));
	}

	public static parseToFilterValue(value: string): { value: number, rangeUnit: string } {
		const lastChar = value.substr(value.length - 1);
		const numberValue = Number(value.substr(0, value.length - 1));
		return {value: numberValue, rangeUnit: DurationMap[lastChar]}
	}

	public static parseFromFilterValue(value: string): string {
		return _.invert(DurationMap)[value];
	}
}

export const DurationMap: { [key: string]: string } = {
	m: "MINUTES",
	h: "HOURS",
	d: "DAYS",
	w: "WEEKS",
	M: "MONTHS",
	Q: "QUARTERS",
	y: "YEARS"
};